import { useState } from "react";
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';

import { deleteProject } from "../../api/projects";
import { CloneProjectModal } from "../CloneProjectModal";
import ConfirmationDialog from "../ConfirmationDialog";
import { ProjectTimezoneSettings } from "./ProjectTimezoneSettings";

import { FaCog, FaArchive, FaClipboardList } from 'react-icons/fa';

import { getTableHeaderStyle } from '../../styles/designSystem';
import {
  useCanDeleteProject,
  useCanManageProjectSettings,
  useProjectAccessContext,
} from '../../context/projectAccess';

interface ProjectDescriptionProps {
  projectData: any;
  projectId: string;
  onProjectUpdate?: () => void;
  projectUsers?: ProjectUserWithRole[];
}


export const ProjectDescription = ({ projectData, projectId, onProjectUpdate, projectUsers }: ProjectDescriptionProps) => {
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const navigate = useNavigate();
  const { error: projectAccessError } = useProjectAccessContext();

  // Determine current user's role in the project
  const currentUserEmail = keycloak?.tokenParsed?.email;
  const currentUserSub = keycloak?.tokenParsed?.sub;
  const currentProjectUser = projectUsers?.find(
    pu => pu.user?.email === currentUserEmail || pu.user_id === currentUserSub
  );
  const isProjectOwner = projectData?.owner_id === currentUserSub || projectData?.owner_id === currentProjectUser?.user_id;
  const allowDelete = useCanDeleteProject(isProjectOwner);
  const allowSettingsEdit = useCanManageProjectSettings(isProjectOwner);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  const handleDeleteProject = async () => {
    if (!token || !projectId) {
      toast.error('Authentication required');
      return;
    }

    setIsDeleting(true);
    try {
      console.log('📦 Archiving project:', projectData.name);
      await deleteProject(token, projectId);
      toast.success(`Project "${projectData.name}" archived successfully`);
      setShowDeleteModal(false);
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to archive project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className="bg-white border border-[#eaeaea] rounded-lg transition-shadow duration-200">
            {/* Header Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <FaCog className="text-[#444] w-5 h-5" />
                    <h2 className="text-xl font-semibold">
                      Project Settings
                    </h2>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="flex-1">
                  Manage project settings and lifecycle
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white p-6 space-y-8">

          {projectAccessError && (
            <div className="border border-[#eaeaea] bg-[#fafafa] rounded px-4 py-3 text-sm text-[#b45309]" role="status">
              Couldn't load your permissions, so project settings are read-only. Reload to try again.
            </div>
          )}

          <ProjectTimezoneSettings
            projectId={projectId}
            projectData={projectData}
            canEdit={allowSettingsEdit}
            onProjectUpdate={onProjectUpdate}
          />

          {/* Clone Project Section */}
          <div className="border-t border-[#eaeaea]"></div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <FaClipboardList className="text-blue-600 w-5 h-5" />
              <h3 className="text-lg font-semibold text-blue-900">Clone Project</h3>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-blue-900 font-medium">Create a Copy</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Clone this project as a new project. You can choose which project attributes to copy.
                  </p>
                </div>
                <button
                  onClick={() => setIsCloneModalOpen(true)}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <FaClipboardList className="w-3 h-3" />
                  <span>Clone Project</span>
                </button>
              </div>
            </div>
          </div>

          {/* Danger Zone - only for admins / project owner */}
          {allowDelete && (<div className="border-t border-[#eaeaea]"></div>)}
          {allowDelete && (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <FaArchive className="text-red-600 w-5 h-5" />
              <h3 className="text-lg font-semibold text-red-900">Danger Zone</h3>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-red-900 font-medium">Archive Project</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This action will archive the project and hide it from active views. Archived projects can be restored later.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(true);
                  }}
                  className="ml-4 px-4 py-2 bg-[#dc2626] text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <FaArchive className="w-3 h-3" />
                  <span>Archive Project</span>
                </button>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Archive Project Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={handleDeleteProject}
        title="Archive Project"
        message={`You are about to archive "${projectData.name}". The project will be hidden from active views but can be restored later.`}
        confirmText="Archive Project"
        cancelText="Cancel"
        confirmButtonClass="bg-[#dc2626] hover:bg-[#b91c1c]"
        requireTextConfirmation={true}
        textToConfirm={projectData.name}
        warningItems={[
          "Project will be hidden from dashboard and lists",
          "Team members will lose access",
          "Data is preserved and can be restored"
        ]}
        isLoading={isDeleting}
      />

      {/* Clone Project Modal */}
      <CloneProjectModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        sourceProject={projectData}
        onCloned={(newProjectId) => {
          navigate(`/projects/${newProjectId}`);
        }}
      />
    </div>
  );
};